import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableRow,
  TableCell,
  TextRun,
  WidthType,
  AlignmentType,
  BorderStyle,
  ShadingType,
  HeadingLevel,
  Header,
  Footer,
  PageNumber,
  ImageRun,
  Tab,
  TabStopType,
} from "docx";
import { saveAs } from "file-saver";
import type { TermsheetData } from "../data/termsheetData";

interface CustomRow {
  label: string;
  value: string;
}

/** Options to align DOCX with drawer logic. */
export interface TermsheetDocxOptions {
  gstPassbackEnabled?: boolean;
  isQuarterly?: boolean;
}

// Footer: brighter than (139, 115, 85) for visibility
const FOOTER_COLOR = "B49B78"; // RGB (180, 155, 120)
// finalPTPM / Rental Cost row highlight
const HIGHLIGHT_FILL = "F8F6F1"; // RGB (248, 246, 241)

const BODY_FONT = "Montserrat";
const BLACK_COLOR = "000000";
const BODY_SIZE = 18; // 9pt (docx uses half-points)
const FOOTER_SIZE = 18; // 9pt — fits company name on one line in equal footer columns

/** Convert data URL to Uint8Array for docx ImageRun. */
function dataUrlToBuffer(dataUrl: string): Uint8Array {
  const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, "");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function getProgramLimitNumeric(value: string | null | undefined): number {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

function getProgramLimitInRupees(value: string | null | undefined): number {
  const str = (value != null ? String(value) : "").toLowerCase();
  const num = getProgramLimitNumeric(value);
  if (str.includes("crore") || str.includes("cr")) return num * 1_00_00_000;
  if (str.includes("lakh") || str.includes("lac")) return num * 1_00_000;
  return num;
}

function getPct(value: string | null | undefined): number {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

function formatInrLakhsCrores(num: number): string {
  if (num >= 1_00_00_000) {
    const cr = num / 1_00_00_000;
    return `INR ${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2)} crores`;
  }
  if (num >= 1_00_000) {
    const l = num / 1_00_000;
    return `INR ${l % 1 === 0 ? l.toFixed(0) : l.toFixed(2)} lakhs`;
  }
  return `INR ${num.toLocaleString("en-IN")}`;
}

function formatIndianCurrency(value: string | number | null | undefined): string {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  if (Number.isNaN(num)) return str;
  if (num >= 1_00_00_000) {
    const cr = num / 1_00_00_000;
    return `₹${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2)} Cr`;
  }
  if (num >= 1_00_000) {
    const l = num / 1_00_000;
    return `₹${l % 1 === 0 ? l.toFixed(0) : l.toFixed(2)} L`;
  }
  return `₹${num.toLocaleString("en-IN")}`;
}

function formatTenureValue(raw: string, isQuarterly: boolean): string {
  const cleaned = String(raw).replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  if (Number.isNaN(num)) return raw;
  if (isQuarterly) {
    const quarters = Math.round(num / 3);
    return `${quarters} ${quarters === 1 ? "Quarter" : "Quarters"}`;
  }
  const months = Math.round(num);
  return `${months} ${months === 1 ? "Month" : "Months"}`;
}

function formatDateDDMMYYYY(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export async function generateTermsheetDocx(
  data: TermsheetData,
  customRows: CustomRow[] = [],
  title?: string,
  logoDataUrl?: string,
  options?: TermsheetDocxOptions
) {
  const borderStyle = {
    top: { style: BorderStyle.SINGLE, size: 1, color: "cccccc" },
    bottom: { style: BorderStyle.SINGLE, size: 1, color: "cccccc" },
    left: { style: BorderStyle.SINGLE, size: 1, color: "cccccc" },
    right: { style: BorderStyle.SINGLE, size: 1, color: "cccccc" },
  };

  const cellMargins = {
    top: 80,
    bottom: 80,
    left: 120,
    right: 120,
  };

  const isQuarterly =
    options?.isQuarterly ?? String(data.paymentFrequency || "").toLowerCase().includes("quarterly");
  const gstPassbackEnabled = options?.gstPassbackEnabled !== false;

  const tenureLabel = isQuarterly ? "Tenure of Lease (In Quarter)" : "Tenure of Lease (In Months)";
  const rentalCostLabel = isQuarterly ? "Rental Cost - PTPQ (Excl GST)" : "Rental Cost - PTPM (Excl GST)";
  const finalLabel = isQuarterly ? "Final - PTPQ (Excl. GST)" : "Final - PTPM (Excl. GST)";

  const programLimitRupees = getProgramLimitInRupees(data.programLimit);
  const securityPct = getPct(data.securityDeposit);
  const processingPct = getPct(data.processingFees);
  const securityAmount =
    securityPct > 0 && programLimitRupees > 0 ? programLimitRupees * (securityPct / 100) : null;
  const processingAmount =
    processingPct > 0 && programLimitRupees > 0 ? programLimitRupees * (processingPct / 100) : null;
  const securityDepositDisplay =`${data.securityDeposit}`
    // securityAmount != null ? `${data.securityDeposit} (${formatInrLakhsCrores(securityAmount)})` : data.securityDeposit;
  const processingFeesDisplay =
    processingAmount != null
      ? `${data.processingFees} (${formatInrLakhsCrores(processingAmount)} + GST)`
      : data.processingFees;

  const makeRow = (label: string, value: string | number | null | undefined, highlight = false) => {
    const str = value != null ? String(value) : "";
    const fillColor = highlight ? HIGHLIGHT_FILL : "F5F5F8";
    return new TableRow({
      children: [
        new TableCell({
          width: { size: 35, type: WidthType.PERCENTAGE },
          borders: borderStyle,
          margins: cellMargins,
          shading: { type: ShadingType.SOLID, color: fillColor },
          children: [new Paragraph({ spacing: { before: 40, after: 40 }, children: [new TextRun({ text: label, bold: true, size: BODY_SIZE, font: BODY_FONT })] })],
        }),
        new TableCell({
          width: { size: 65, type: WidthType.PERCENTAGE },
          borders: borderStyle,
          margins: cellMargins,
          shading: highlight ? { type: ShadingType.SOLID, color: HIGHLIGHT_FILL } : undefined,
          children: str.split("\n").map(
            (line) => new Paragraph({ spacing: { before: 40, after: 40 }, children: [new TextRun({ text: line, bold: highlight, size: BODY_SIZE, font: BODY_FONT })] })
          ),
        }),
      ],
    });
  };

  const rows: [string, string, boolean?][] = [
    ["Lessee Name", data.lesseeName],
    ["Lessor", data.lessor],
    [tenureLabel, formatTenureValue(data.tenureOfLease, isQuarterly)],
    ["Program Limit", formatIndianCurrency(data.programLimit)],
    ["Security Deposit", securityDepositDisplay],
    [rentalCostLabel, `${data.rentalCostPTPM}`, !gstPassbackEnabled],
  ];
  if (gstPassbackEnabled) {
    rows.push(["GST Rebate", `${data.gstRebate}`]);
    rows.push([finalLabel, `${data.finalPTPM}`, true]);
  }
  if (processingPct > 0) {
    rows.push(["Processing Fees (Excl. GST)", processingFeesDisplay]);
  }
  if (String(data.advanceRental || "").trim() !== "") {
    rows.push(["Advance Rental (Excl. GST)", `${data.advanceRental}`]);
  }
  rows.push(
    ["Payment Frequency", data.paymentFrequency],
    ["Rental Payments", data.rentalPayments],
    ["Pro Rated Rentals", data.proRatedRentals],
    ["Foreclosure Charges", data.foreclosureCharges],
    ["Documentation Charges", data.documentationCharges],
    ["Insurance", data.insurance],
    ["Clear Title and Encumbrances", data.clearTitle],
    ["Economic Life Certification", data.economicLife],
    ["Penal Charges", data.penalCharges],
    ["Additional Security", data.additionalSecurity],
    ["Minimum Performance Guarantee", data.minimumPerformanceGuarantee],
    ["Validity of Sanction", data.validityOfSanction],
    ["SMA/NPA Reporting", data.smaReporting],
    ["Confidentiality", data.confidentiality]
  );

  // Append custom rows (Added Terms)
  customRows.forEach((cr) => {
    if (cr.label || cr.value) {
      rows.push([cr.label || "—", cr.value || "—"]);
    }
  });

  const headerChildren: (Paragraph)[] = [];
  if (logoDataUrl) {
    try {
      const logoBuffer = dataUrlToBuffer(logoDataUrl);
      headerChildren.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 100 },
          children: [
            new ImageRun({
              data: logoBuffer,
              transformation: { width: 150, height: 45 },
              type: "png",
            }),
          ],
        })
      );
    } catch {
      headerChildren.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 100 },
          children: [new TextRun({ text: "Astrafin", bold: true, size: BODY_SIZE, color: "1E3250", font: BODY_FONT })],
        })
      );
    }
  } else {
    headerChildren.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
        children: [new TextRun({ text: "Astrafin", bold: true, size: BODY_SIZE, color: "1E3250", font: BODY_FONT })],
      })
    );
  }
  headerChildren.push(
    new Paragraph({
      border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: FOOTER_COLOR } },
      children: [],
    }),
    new Paragraph({ children: [new TextRun({ text: "", size: BODY_SIZE, font: BODY_FONT })] }),
  );

  const doc = new Document({
    styles: { default: { document: { run: { font: BODY_FONT } } } },
    sections: [
      {
        properties: {
          page: {
            // Slightly wider page so footer three-columns fit like the PDF (L → R)
            margin: { top: 1200, bottom: 1400, left: 720, right: 720 },
          },
        },
        headers: {
          default: new Header({
            children: headerChildren,
          }),
        },
        footers: {
          default: new Footer({
            children: (() => {
              // Tab-based 3 columns — Contact nudged right; Address stays at 2/3
              const footerContentWidth = 10466; // A4 minus 0.5" L/R margins
              const col1 = Math.floor(footerContentWidth / 3) + 700; // Contact Details → right a bit
              const col2 = Math.floor((footerContentWidth * 2) / 3); // Address unchanged
              const footerTabs = [
                { type: TabStopType.LEFT, position: col1 },
                { type: TabStopType.LEFT, position: col2 },
              ];
              const footerRun = (text: string, bold = false) =>
                new TextRun({ text, bold, size: FOOTER_SIZE, color: FOOTER_COLOR, font: BODY_FONT });
              const tab = () => new TextRun({ children: [new Tab()] });

              return [
                new Paragraph({
                  border: { top: { style: BorderStyle.SINGLE, size: 6, color: FOOTER_COLOR } },
                  spacing: { after: 60 },
                  children: [],
                }),
                new Paragraph({
                  tabStops: footerTabs,
                  spacing: { after: 0 },
                  children: [
                    footerRun("Astrafin Neosolutions Pvt. Ltd.", true),
                    tab(),
                    footerRun("Contact Details", true),
                    tab(),
                    footerRun("Address", true),
                  ],
                }),
                new Paragraph({
                  tabStops: footerTabs,
                  spacing: { after: 0 },
                  children: [
                    footerRun("CIN : ", true),
                    footerRun("U66190MH2024PTC432478"),
                    tab(),
                    footerRun("tarun@astrafin.org"),
                    tab(),
                    footerRun("L-504, Greenwoods CHS, Andheri East,"),
                  ],
                }),
                new Paragraph({
                  tabStops: footerTabs,
                  spacing: { after: 0 },
                  children: [
                    tab(),
                    footerRun("+91 98692 17190"),
                    tab(),
                    footerRun("Mumbai, Maharashtra, 400093"),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  spacing: { before: 60 },
                  children: [
                    new TextRun({ size: FOOTER_SIZE, color: FOOTER_COLOR, font: BODY_FONT, children: [PageNumber.CURRENT] }),
                    new TextRun({ size: FOOTER_SIZE, color: FOOTER_COLOR, font: BODY_FONT, text: "/" }),
                    new TextRun({ size: FOOTER_SIZE, color: FOOTER_COLOR, font: BODY_FONT, children: [PageNumber.TOTAL_PAGES] }),
                  ],
                }),
              ];
            })(),
          }),
        },
        children: [
          new Paragraph({ children: [new TextRun({ text: "To,", size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ children: [new TextRun({ text: data.lesseeName, size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ children: [new TextRun({ text: data.lesseeAddress, size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ children: [new TextRun({ text: `Date: ${formatDateDDMMYYYY(data.date)}`, size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ text: "" }),
          new Paragraph({ children: [new TextRun({ text: `Ref. No: ${data.refNo}`, size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ text: "" }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            heading: HeadingLevel.HEADING_2,
            children: [new TextRun({ text: title || "Indicative Termsheet", bold: true, size: BODY_SIZE, color: BLACK_COLOR, font: BODY_FONT })],
          }),
          new Paragraph({ text: "" }),
          new Paragraph({
            children: [new TextRun({ text: `Sub: Asset Leasing Facility for ${data.lesseeName}`, bold: true, size: BODY_SIZE, underline: {}, font: BODY_FONT })],
          }),
          new Paragraph({ text: "" }),
          new Paragraph({
            children: [new TextRun({ text: "Astrafin Neosolutions Private Limited hereby offers an asset leasing facility, subject to the terms and conditions set out below.", size: BODY_SIZE, font: BODY_FONT })],
          }),
          new Paragraph({ text: "" }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: rows.map((r) => makeRow(r[0], r[1], r[2])),
          }),
          new Paragraph({ text: "" }),
          new Paragraph({
            children: [new TextRun({ text: `Note: ${data.note}`, italics: true, size: BODY_SIZE, font: BODY_FONT })],
          }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ children: [new TextRun({ text: "Accepted and Signed by", size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ text: "" }),
          new Paragraph({ children: [new TextRun({ text: "_______________________________________", size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ spacing: { before: 100 }, children: [new TextRun({ text: "Name:", size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ spacing: { before: 100 }, children: [new TextRun({ text: "Designation:", size: BODY_SIZE, font: BODY_FONT })] }),
          new Paragraph({ spacing: { before: 100 }, children: [new TextRun({ text: "Date:", size: BODY_SIZE, font: BODY_FONT })] }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);

  // Generate filename: companyName_TS_{date}_{tenure}{M|Q}.docx
   // Generate filename: companyName_TS_{date}_{tenure}{M|Q}.pdf
   const generateFileName = () => {
    // Format date as ddMMMyy (e.g., 25Dec25)
  
  // Get company name, sanitize for filename (remove special chars, spaces)
  const sanitizedCompanyName = (data.lesseeName || 'Company')
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  
  // Get tenure months
  let tenureMonths = 0;
  let frequencySuffix = '';
  if(data.paymentFrequency?.toLowerCase() === 'quarterly in advance'){
    tenureMonths = parseInt(data.tenureOfLease) / 3;
    frequencySuffix = 'Q';
  }
  else{
    tenureMonths = parseInt(data.tenureOfLease);
    frequencySuffix = 'M';
  }
  return `${sanitizedCompanyName}_TS_${data.date}_${tenureMonths}${frequencySuffix}.docx`;
};

  saveAs(blob, generateFileName());
}
